import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Copy, GripVertical, Info, Lock, Plus, ScrollText, Trash2, Upload, X } from "lucide-react";
import { api, fileUrl, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "sonner";

function slugify(s) {
  return (s || "").normalize("NFKD").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toUpperCase() || "SKU";
}

export default function ProductEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);
  const [tierTpls, setTierTpls] = useState([]);
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmRemoveVariant, setConfirmRemoveVariant] = useState(null); // variant key when a saved size is about to be removed

  const [form, setForm] = useState({
    name: "", description: "", internal_notes: "", category_id: "",
    is_own_cross: false,
    propagation_type: "none", // "none" | "seed_derived" | "clone"
    seed_parent: null, pollen_parent: null,
    photos: [],
    status: "draft",
    variants: [], // { local key, id?, size_option_id, sku, base_price, partner_price, stock_qty, tier_template_id, overrides: {country_id: price} }
  });

  const load = useCallback(async () => {
    const [c, so, tt, ct] = await Promise.all([
      api.get("/categories"), api.get("/size-options"),
      api.get("/tier-templates"), api.get("/countries"),
    ]);
    setCategories(c.data); setSizeOpts(so.data); setTierTpls(tt.data); setCountries(ct.data);

    if (!isNew) {
      const { data } = await api.get(`/products/${id}`);
      setForm({
        name: data.name, description: data.description || "", internal_notes: data.internal_notes || "", category_id: data.category_id || "",
        is_own_cross: !!data.is_own_cross,
        propagation_type: data.propagation_type || "none",
        seed_parent: data.seed_parent || null, pollen_parent: data.pollen_parent || null,
        photos: data.photos || [], status: data.status,
        variants: (data.size_variants || []).map((v) => ({
          key: v.id, id: v.id,
          size_option_id: v.size_option_id, sku: v.sku, base_price: v.base_price,
          partner_price: v.partner_price ?? "",
          stock_qty: v.stock_qty, tier_template_id: v.tier_template_id || "",
          display_qty_override: v.display_qty_override ?? "",
          overrides: Object.fromEntries((v.overrides || []).map((o) => [o.country_id, o.price])),
        })),
      });
    }
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  const isSizeChecked = (size_id) => form.variants.some((v) => v.size_option_id === size_id);
  const toggleSize = (size) => {
    if (isSizeChecked(size.id)) {
      set({ variants: form.variants.filter((v) => v.size_option_id !== size.id) });
    } else {
      const sku = `${slugify(form.name || "PRODUCT")}-${slugify(size.name)}`;
      set({ variants: [...form.variants, {
        key: `new-${size.id}-${Date.now()}`, size_option_id: size.id, sku,
        base_price: 0, stock_qty: 0, tier_template_id: "", display_qty_override: "", overrides: {},
      }] });
    }
  };

  const updateVariant = (key, patch) => set({ variants: form.variants.map((v) => v.key === key ? { ...v, ...patch } : v) });

  const submit = async (targetStatus) => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    const body = {
      name: form.name.trim(),
      description: form.description,
      internal_notes: form.internal_notes,
      category_id: form.category_id || null,
      is_own_cross: form.is_own_cross,
      propagation_type: form.propagation_type === "none" ? null : form.propagation_type,
      seed_parent: form.seed_parent,
      pollen_parent: form.pollen_parent,
      photos: form.photos,
      status: targetStatus,
      size_variants: form.variants.map((v) => {
        const raw = String(v.display_qty_override ?? "").trim();
        const displayOv = raw === "" ? null : Number(raw);
        const partnerRaw = String(v.partner_price ?? "").trim();
        return {
          id: v.id, size_option_id: v.size_option_id, sku: v.sku,
          base_price: Number(v.base_price) || 0,
          partner_price: partnerRaw === "" ? null : Number(partnerRaw),
          stock_qty: Number(v.stock_qty) || 0,
          tier_template_id: v.tier_template_id || null,
          display_qty_override: displayOv,
          overrides: Object.entries(v.overrides || {})
            .filter(([, p]) => p !== "" && p !== null && p !== undefined)
            .map(([country_id, price]) => ({ country_id, price: Number(price) })),
        };
      }),
    };
    try {
      const res = isNew
        ? await api.post("/products", body)
        : await api.put(`/products/${id}`, body);
      toast.success(isNew ? "Product created" : "Product saved");
      navigate(`/admin/products/${res.data.id}`);
      // If we came from /new, reload once so state has server IDs.
      if (isNew) load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        title={isNew ? "New product" : form.name || "Edit product"}
        subtitle="One form: identity, lineage, photos, sizes with pricing and country overrides."
        testid="page-product-edit"
        actions={
          <>
            <Link to="/admin/products"><Button variant="outline" className="h-9" data-testid="product-cancel-btn">Cancel</Button></Link>
            <Button data-testid="product-save-draft-btn" disabled={busy} onClick={() => submit("draft")} variant="outline" className="h-9">Save as draft</Button>
            <Button data-testid="product-publish-btn" disabled={busy} onClick={() => submit("published")} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">Publish</Button>
          </>
        }
      />
      <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
        {/* Identity */}
        <section className="bg-white border border-[#E2E4E0] rounded-lg p-5">
          <h2 className="db-h font-semibold mb-3">Identity</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs text-[#5C665A]">Name</label>
              <Input data-testid="pe-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className="h-9" />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Category</label>
              <Select value={form.category_id || "__none__"} onValueChange={(v) => set({ category_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="pe-category"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-[#5C665A]">Description</label>
              <Textarea data-testid="pe-description" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} />
              <div className="text-[11px] text-[#5C665A] mt-1">Shown on the buyer catalog and product page.</div>
            </div>
            <div className="md:col-span-3">
              <div className="bg-[#FBF7E6] border border-[#EFE1A6] rounded-md p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Lock size={12} className="text-[#8A5A2B]" />
                  <span className="text-xs font-semibold text-[#8A5A2B]">Internal notes · admin only</span>
                  <span className="text-[10px] text-[#8A5A2B]/70">Never shown to buyers or on PDFs</span>
                </div>
                <Textarea
                  data-testid="pe-internal-notes"
                  value={form.internal_notes}
                  onChange={(e) => set({ internal_notes: e.target.value })}
                  rows={3}
                  placeholder="Import metadata, export prices, private agronomy notes…"
                  className="bg-white border-[#EFE1A6] focus-visible:ring-[#D4A373]"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid="pe-cross" checked={form.is_own_cross} onCheckedChange={(v) => set({ is_own_cross: !!v })} />
              <span className="font-medium">DocBlock Cross</span>
              <span className="text-xs text-[#5C665A]">(own hybrid)</span>
            </label>
            <div className="inline-flex items-center gap-2 text-sm">
              <span className="font-medium">Propagation</span>
              <div className="inline-flex rounded-md border border-[#E2E4E0] overflow-hidden" role="group" data-testid="pe-propagation">
                {[
                  { v: "none", label: "None" },
                  { v: "seed_derived", label: "Seed Derived" },
                  { v: "clone", label: "Clone" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set({ propagation_type: opt.v })}
                    className={`px-2.5 h-8 text-xs transition-colors ${form.propagation_type === opt.v ? "bg-[#2D6A4F] text-white" : "bg-white text-[#1A1D1A] hover:bg-[#F1F5F1]"}`}
                    data-testid={`pe-propagation-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {form.is_own_cross && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E2E4E0]">
              <ParentField
                label="Seed parent" testid="pe-seed"
                value={form.seed_parent} onChange={(v) => set({ seed_parent: v })} excludeId={id}
              />
              <ParentField
                label="Pollen parent" testid="pe-pollen"
                value={form.pollen_parent} onChange={(v) => set({ pollen_parent: v })} excludeId={id}
              />
            </div>
          )}
        </section>

        {/* Photos */}
        <section className="bg-white border border-[#E2E4E0] rounded-lg p-5">
          <h2 className="db-h font-semibold mb-3">Photos</h2>
          <PhotoUploader photos={form.photos} onChange={(next) => set({ photos: next })} />
        </section>

        {/* Sizes */}
        <section className="bg-white border border-[#E2E4E0] rounded-lg p-5">
          <h2 className="db-h font-semibold mb-3">Sizes &amp; pricing</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {sizeOpts.map((s) => (
              <label key={s.id} className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border transition-colors cursor-pointer ${isSizeChecked(s.id) ? "border-[#2D6A4F] bg-[#E9F5EC] text-[#1B4332]" : "border-[#E2E4E0] hover:bg-[#F9F9F8]"}`}>
                <Checkbox data-testid={`pe-size-check-${s.id}`} checked={isSizeChecked(s.id)} onCheckedChange={() => toggleSize(s)} />
                <span className="text-sm font-medium">{s.name}</span>
                {s.note && <span className="text-[11px] text-[#5C665A]/80" data-testid={`pe-size-note-${s.id}`}>({s.note})</span>}
              </label>
            ))}
          </div>
          <div className="space-y-3">
            {form.variants.length === 0 && <div className="text-sm text-[#5C665A]">Check a size above to configure its price and stock.</div>}
            {form.variants.map((v) => {
              const so = sizeOpts.find((s) => s.id === v.size_option_id);
              return (
              <VariantRow
                key={v.key} variant={v}
                sizeName={so?.name}
                sizeNote={so?.note || ""}
                tierTpls={tierTpls} countries={countries}
                onChange={(patch) => updateVariant(v.key, patch)}
                onRemove={() => {
                  // If this variant has a persisted id (saved size), ask for
                  // confirmation before removing — it may carry stock, SKU or
                  // country overrides that would be lost on Publish/Save.
                  if (v.id) setConfirmRemoveVariant(v.key);
                  else set({ variants: form.variants.filter((x) => x.key !== v.key) });
                }}
              />
              );
            })}
          </div>
        </section>
      </div>
      {/* Size-row delete confirmation (only for previously-saved variants) */}
      <ConfirmDialog
        open={!!confirmRemoveVariant}
        onOpenChange={(o) => !o && setConfirmRemoveVariant(null)}
        title="Remove this size?"
        description="Removing this size on the next save will delete its SKU, stock and country overrides. This cannot be undone from the UI."
        confirmLabel="Remove size"
        destructive
        onConfirm={() => {
          set({ variants: form.variants.filter((x) => x.key !== confirmRemoveVariant) });
          setConfirmRemoveVariant(null);
        }}
        testid="pe-confirm-remove-variant"
      />
    </>
  );
}

/* -------------------- Parent autocomplete -------------------- */

function ParentField({ label, value, onChange, testid, excludeId }) {
  const [q, setQ] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => { setQ(value?.name || (value?.product_id ? "" : "")); }, [value?.product_id, value?.name]);
  useEffect(() => {
    if (!focused) return;
    const t = setTimeout(async () => {
      // Union: registry entries + ALL products (including drafts/archived).
      const params = new URLSearchParams({ q });
      if (excludeId && excludeId !== "new") params.set("exclude_id", excludeId);
      const { data } = await api.get(`/admin/parents/autocomplete/search?${params.toString()}`);
      setResults(data); setOpen(true);
    }, 150);
    return () => clearTimeout(t);
  }, [q, focused, excludeId]);

  const pickResult = (r) => {
    // Registry rows (or registry+linked): if the row has a linked_product_id
    // we prefer the product link. Registry-only rows store free-text name.
    if (r.source === "product") {
      onChange({ product_id: r.id, name: r.name });
    } else if (r.linked_product_id) {
      onChange({ product_id: r.linked_product_id, name: r.name });
    } else {
      onChange({ product_id: null, name: r.name });
    }
    setQ(r.name);
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="text-xs text-[#5C665A]">{label}</label>
      <Input
        data-testid={testid}
        value={q}
        onChange={(e) => { setQ(e.target.value); onChange({ product_id: null, name: e.target.value || null }); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setTimeout(() => { setOpen(false); setFocused(false); }, 150)}
        placeholder="Type to search or enter free-text name"
        className="h-9"
      />
      <div className="text-[10px] text-[#5C665A] mt-0.5">
        Searches registry + all products (draft/archived included). Free-text names are added to the registry on save.
      </div>
      {value?.product_id && (
        <Badge className="mt-1 bg-[#E9F5EC] text-[#1B4332] border-transparent" data-testid={`${testid}-linked`}>linked</Badge>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-[#E2E4E0] rounded-md shadow-sm max-h-52 overflow-auto" data-testid={`${testid}-results`}>
          {results.map((r) => (
            <button key={`${r.source}-${r.id}`} type="button"
              className="flex items-center justify-between gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-[#F9F9F8]"
              onMouseDown={(e) => { e.preventDefault(); pickResult(r); }}
              data-testid={`${testid}-option-${r.id}`}
            >
              <span>{r.name}</span>
              <span className="inline-flex items-center gap-1 text-[10px] text-[#5C665A]">
                {r.source === "product" && (
                  <Badge className={`border-transparent ${r.status === "published" ? "bg-[#E9F5EC] text-[#1B4332]" : "bg-neutral-100 text-neutral-600"}`}>
                    {r.status || "product"}
                  </Badge>
                )}
                {r.source === "registry" && !r.linked_product_id && (
                  <Badge className="bg-[#FBF7E6] text-[#8A5A2B] border-transparent">registry</Badge>
                )}
                {r.source === "registry" && r.linked_product_id && (
                  <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent">registry + product</Badge>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Photo uploader -------------------- */

function PhotoUploader({ photos, onChange }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const dragFromIndex = useRef(null);

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    const uploaded = [];
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    onChange([...(photos || []), ...uploaded]);
    if (uploaded.length) toast.success(`Uploaded ${uploaded.length} photo${uploaded.length > 1 ? "s" : ""}`);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const move = (from, to) => {
    if (from === to) return;
    const next = [...photos];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div>
      <div
        className={`border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${dragOver ? "border-[#2D6A4F] bg-[#E9F5EC]" : "border-[#E2E4E0] bg-[#F9F9F8] hover:bg-white"}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="pe-photo-dropzone"
      >
        <Upload size={20} className="mx-auto text-[#5C665A] mb-1" />
        <div className="text-sm text-[#1A1D1A]">Drag &amp; drop images here, or <span className="text-[#2D6A4F] font-medium">click to browse</span></div>
        <div className="text-xs text-[#5C665A] mt-1">JPG, PNG, WebP · max 8 MB each</div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="pe-photo-input" />
      </div>
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="pe-photo-grid">
          {photos.map((p, idx) => (
            <div
              key={p.storage_path + idx}
              className="relative group border border-[#E2E4E0] rounded-md overflow-hidden bg-[#F9F9F8]"
              draggable
              onDragStart={() => { dragFromIndex.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); move(dragFromIndex.current, idx); }}
              data-testid={`pe-photo-${idx}`}
            >
              <img src={fileUrl(p.storage_path)} alt="" className="w-full h-32 object-contain bg-[#F9F9F8]" />
              <div className="absolute inset-x-0 top-0 flex items-center justify-between p-1 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="inline-flex items-center gap-1 text-white text-xs px-1"><GripVertical size={12} /> {idx === 0 ? "primary" : `#${idx + 1}`}</span>
                <button
                  className="text-white hover:text-red-200"
                  onClick={() => onChange(photos.filter((_, i) => i !== idx))}
                  data-testid={`pe-photo-remove-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Variant row -------------------- */

function VariantRow({ variant, sizeName, sizeNote = "", tierTpls, countries, onChange, onRemove }) {
  const [openOv, setOpenOv] = useState(false);
  const overridesCount = Object.values(variant.overrides || {}).filter((p) => p !== "" && p !== null && p !== undefined).length;
  const selectedTpl = tierTpls.find((t) => t.id === variant.tier_template_id);
  const tplHasFixed = !!selectedTpl?.rows?.some((r) => r.fixed_price != null);
  return (
    <div className="border border-[#E2E4E0] rounded-md" data-testid={`pe-variant-${variant.size_option_id}`}>
      <div className="grid grid-cols-2 md:grid-cols-[110px_1fr_100px_100px_90px_90px_1fr_auto] gap-3 items-end px-3 py-2">
        <div className="col-span-2 md:col-span-1">
          <div className="text-xs text-[#5C665A]">Size</div>
          <div className="text-sm font-medium h-9 flex items-center gap-1.5">
            <span>{sizeName}</span>
            {sizeNote && <span className="text-[11px] text-[#5C665A]/80 font-normal">({sizeNote})</span>}
          </div>
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className="text-xs text-[#5C665A]">SKU</label>
          <Input value={variant.sku} onChange={(e) => onChange({ sku: e.target.value })} className="h-9 db-mono" data-testid={`pe-var-sku-${variant.size_option_id}`} />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Base € (excl VAT)</label>
          <Input type="number" step="0.01" value={variant.base_price} onChange={(e) => onChange({ base_price: e.target.value })} className="h-9 text-right db-mono" data-testid={`pe-var-price-${variant.size_option_id}`} />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]" title="Flat partner price for Official Vendor buyers. Empty = fall back to normal pricing.">Partner €</label>
          <Input type="number" step="0.01" value={variant.partner_price ?? ""} onChange={(e) => onChange({ partner_price: e.target.value })} className="h-9 text-right db-mono" placeholder="—" data-testid={`pe-var-partner-${variant.size_option_id}`} />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Stock</label>
          <Input type="number" value={variant.stock_qty} onChange={(e) => onChange({ stock_qty: e.target.value })} className="h-9 text-right db-mono" data-testid={`pe-var-stock-${variant.size_option_id}`} />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]" title="Banded display override (min 1). Blank = auto band.">Display</label>
          <Input
            type="number" min={1} placeholder="auto"
            value={variant.display_qty_override}
            onChange={(e) => onChange({ display_qty_override: e.target.value })}
            className="h-9 text-right db-mono"
            data-testid={`pe-var-display-${variant.size_option_id}`}
          />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Tier template</label>
          <Select value={variant.tier_template_id || "__none__"} onValueChange={(v) => onChange({ tier_template_id: v === "__none__" ? "" : v })}>
            <SelectTrigger className="h-9" data-testid={`pe-var-tier-${variant.size_option_id}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— none —</SelectItem>
              {tierTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {tplHasFixed && (
            <div className="text-[10px] text-[#8A5A2B] inline-flex items-center gap-1 mt-1" data-testid={`pe-var-tier-fixed-${variant.size_option_id}`}>
              <Info size={10} /> Contains fixed prices — skips country %
            </div>
          )}
        </div>
        <button
          onClick={onRemove}
          className="text-[#C94A4A] hover:bg-[#FDF3F3] h-9 w-9 grid place-items-center rounded-md justify-self-end col-span-2 md:col-span-1"
          data-testid={`pe-var-remove-${variant.size_option_id}`}
          title="Remove size"
        >
          <Trash2 size={14} />
        </button>
      </div>
      {variant.id && (
        <div className="px-3 pb-2 -mt-1 text-xs">
          <Link
            to={`/admin/stock-logs?size_variant_id=${variant.id}`}
            className="inline-flex items-center gap-1 text-[#5C665A] hover:text-[#1B4332] underline underline-offset-2"
            data-testid={`pe-var-stocklog-${variant.size_option_id}`}
          >
            <ScrollText size={12} /> View stock log
          </Link>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpenOv(!openOv)}
        className="w-full text-left px-3 py-2 border-t border-[#E2E4E0] bg-[#F9F9F8] text-xs text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-2"
        data-testid={`pe-var-overrides-toggle-${variant.size_option_id}`}
      >
        {openOv ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Country price overrides {overridesCount ? <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent">{overridesCount}</Badge> : null}
      </button>
      {openOv && (
        <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-3 gap-3 border-t border-[#E2E4E0]">
          {countries.map((c) => (
            <div key={c.id}>
              <label className="text-xs text-[#5C665A]">{c.name} <span className="opacity-60">({c.percent_adjustment >= 0 ? "+" : ""}{c.percent_adjustment}%)</span></label>
              <Input
                type="number" step="0.01" placeholder="—"
                value={variant.overrides?.[c.id] ?? ""}
                onChange={(e) => onChange({ overrides: { ...(variant.overrides || {}), [c.id]: e.target.value === "" ? "" : e.target.value } })}
                className="h-9 text-right db-mono"
                data-testid={`pe-override-${variant.size_option_id}-${c.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// suppress import warning
void Copy; void Plus;
